/**
 * How a mapped entity is identified — the one decision that says which rows, and which files,
 * end up as the same entity in the case. Pure: the recipe and the preview in, a description out.
 *
 * The projection composes an entity's identifier from its identity columns in their declared
 * order, joined with `|`, with the identity scope as a constant prefix; entities with the same
 * identifier become one entity when merging is enabled. With merging off, descriptors stay
 * separate per row and constant subjects stay local to the table; their source values remain data.
 */

import type {
  DescriptorPreview,
  EntityDescriptor,
  SubjectEntity,
} from '@/services/api/structuredIngest'

/** Known identity rules, by the phrase the screen uses for them. */
type IdentityRule =
  | 'exact'
  | 'e164'
  | 'cz_account'
  | 'iban'
  | 'extract'
  | 'name_and_id'
  | 'bank_code'

export type Identity =
  | {
      kind: 'columns'
      columns: string[]
      /** Constant prefix of every identifier (`identity_scope`). */
      scope: string | null
      rule: IdentityRule | null
      /** Graph property the identifier is stored in and matched on. */
      property: string | null
      /** Identifiers as the preview composed them, a few real ones. */
      examples: string[]
      /**
       * A social post number without its channel/account namespace. Numbering can restart
       * in another source; numeric phone numbers or other schema identifiers are not this case.
       */
      numbersOnly: boolean
    }
  | { kind: 'constant'; value: string | null; property: string | null }
  | { kind: 'perRow'; mergeDisabled?: boolean }
  | { kind: 'perFile' }
  | { kind: 'missing'; property: string | null; reason?: 'constant' }
  | { kind: 'invalid'; property: string | null; error: string | null; allowed: string[] }
  | { kind: 'pending'; property: string | null; reason: 'validation' | 'approval' }

type IdentityItem = Pick<
  SubjectEntity,
  | 'unique_property'
  | 'identity_properties'
  | 'identity_error'
  | 'schema_match'
  | 'accept_new_type'
  | 'merge_enabled'
>

/** Local edits must not turn an unvalidated property into a displayed merge key. */
function identityProblem(item: IdentityItem): Identity | null {
  const property = item.unique_property?.trim() || null
  if (item.identity_error) {
    return {
      kind: 'invalid',
      property,
      error: item.identity_error,
      allowed: item.identity_properties ?? [],
    }
  }
  if (!property) return { kind: 'missing', property }
  if (!item.identity_properties) return { kind: 'pending', property, reason: 'validation' }
  if (!item.identity_properties.includes(property)) {
    return { kind: 'invalid', property, error: null, allowed: item.identity_properties }
  }
  if (item.schema_match === false && !item.accept_new_type) {
    return { kind: 'pending', property, reason: 'approval' }
  }
  return null
}

export function verifiedIdentityProperty(item: IdentityItem): string | null {
  return item.merge_enabled === false || identityProblem(item)
    ? null
    : item.unique_property?.trim() || null
}

export function canEnableMappingMerge(item: SubjectEntity | EntityDescriptor): boolean {
  if (identityProblem(item)) return false
  if ('identity_columns' in item) {
    return (
      item.identity_rule?.trim().toLowerCase() !== 'source_row' && item.identity_columns.length > 0
    )
  }
  return !!item.identity_value?.trim()
}

const RULES: Array<[RegExp, IdentityRule]> = [
  [/^(exact|stable_id)$/i, 'exact'],
  [/^e164$/i, 'e164'],
  [/cz_account/i, 'cz_account'],
  [/iban/i, 'iban'],
  [/extract|text/i, 'extract'],
  [/name_and_id/i, 'name_and_id'],
  [/bank_code/i, 'bank_code'],
]

function ruleOf(rule: string | null | undefined): IdentityRule | null {
  if (!rule) return null
  return RULES.find(([pattern]) => pattern.test(rule))?.[1] ?? null
}

/** Independent row storage, either explicitly source-local or with mapping merging disabled. */
export function isSourceRow(
  descriptor: Pick<EntityDescriptor, 'identity_rule' | 'merge_enabled'>
): boolean {
  return (
    descriptor.merge_enabled === false ||
    descriptor.identity_rule?.trim().toLowerCase() === 'source_row'
  )
}

const BARE_NUMBER = /^\d+$/

/** Mirrors the social-post scope of the backend's numeric-post collision check. */
function isUnscopedPostNumber(descriptor: EntityDescriptor, examples: string[]): boolean {
  return (
    descriptor.enabled !== false &&
    descriptor.schema_type.replace(/^new:/, '').trim() === 'SocialMediaPost' &&
    !descriptor.identity_scope?.trim() &&
    examples.length > 0 &&
    examples.every(value => BARE_NUMBER.test(value))
  )
}

export function descriptorIdentity(
  descriptor: EntityDescriptor,
  preview: DescriptorPreview | null
): Identity {
  if (isSourceRow(descriptor) && !descriptor.identity_error)
    return { kind: 'perRow', mergeDisabled: descriptor.merge_enabled === false }
  const problem = identityProblem(descriptor)
  if (problem) return problem
  const columns = descriptor.identity_columns ?? []
  const property = descriptor.unique_property || null
  if (columns.length === 0) return { kind: 'missing', property }
  const scope = descriptor.identity_scope?.trim() || null
  const examples = (preview?.examples ?? []).filter(Boolean).slice(0, 3)
  return {
    kind: 'columns',
    columns,
    scope,
    rule: ruleOf(descriptor.identity_rule),
    property,
    examples,
    numbersOnly: isUnscopedPostNumber(descriptor, examples),
  }
}

export function subjectIdentity(entity: SubjectEntity): Identity {
  if (entity.merge_enabled === false && !entity.identity_error) {
    return entity.identity_value?.trim()
      ? { kind: 'perFile' }
      : { kind: 'missing', property: entity.unique_property || null, reason: 'constant' }
  }
  const problem = identityProblem(entity)
  if (problem) return problem
  if (!entity.identity_value?.trim())
    return { kind: 'missing', property: entity.unique_property || null, reason: 'constant' }
  return {
    kind: 'constant',
    value: entity.identity_value?.trim() || null,
    property: entity.unique_property || null,
  }
}

/** The column every identifying column belongs to, for the column list (enabled entities only). */
export function identifyingColumns(descriptors: EntityDescriptor[]): Map<string, string[]> {
  const owners = new Map<string, string[]>()
  for (const descriptor of descriptors) {
    if (
      descriptor.enabled === false ||
      isSourceRow(descriptor) ||
      !verifiedIdentityProperty(descriptor)
    )
      continue
    for (const column of descriptor.identity_columns ?? []) {
      owners.set(column, [...(owners.get(column) ?? []), descriptor.name])
    }
  }
  return owners
}
